import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Home, ChevronRight, UserCheck, UserX, Shield, Users, Clock,
  Loader2, Search, X, Trash2, KeyRound, UserMinus, UserPlus,
  Edit, Eye, Copy, History, ShieldOff
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return `${formatDate(dateString)} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-green-500/20 text-green-400 border-green-500/30',
    inactive: 'bg-red-500/20 text-red-400 border-red-500/30',
    pending: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-bold uppercase border ${styles[status] || styles.pending}`} data-testid={`status-${status}`}>
      {status}
    </span>
  );
};

const AdminUsers = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [resetResult, setResetResult] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/users?admin_email=${user.email}`);
      setAllUsers(res.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditTrail = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/audit-trail?admin_email=${user.email}&limit=100`);
      setAuditTrail(res.data);
    } catch (error) {
      toast.error('Failed to fetch audit trail');
    }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/approve-user/${userId}?admin_email=${user.email}`);
      toast.success('User approved');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleDeactivate = async (u) => {
    if (!window.confirm(`Deactivate "${u.name}" (${u.email})?\n\nThis will prevent the user from logging in. Their data and history will be preserved.`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/auth/users/${u.id}/deactivate?admin_email=${user.email}`);
      toast.success(`${u.name} deactivated`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const handleActivate = async (u) => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/users/${u.id}/activate?admin_email=${user.email}`);
      toast.success(`${u.name} reactivated`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to activate user');
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`PERMANENTLY DELETE "${u.name}" (${u.email})?\n\nThis action CANNOT be undone. The user's account will be permanently removed.\n\nIf you want to preserve their history, use "Deactivate" instead.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/auth/users/${u.id}?admin_email=${user.email}`);
      toast.success(`${u.name} permanently deleted`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (u) => {
    if (!window.confirm(`Reset password for "${u.name}" (${u.email})?\n\nA temporary password will be generated. You must share it with the user securely.`)) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/users/${u.id}/reset-password?admin_email=${user.email}`);
      setResetResult(res.data);
      toast.success('Password reset successful');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleMakeAdmin = async (u) => {
    if (!window.confirm(`Grant admin privileges to "${u.name}"?`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/auth/make-admin/${u.id}?admin_email=${user.email}`);
      toast.success(`${u.name} is now an admin`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to grant admin');
    }
  };

  const handleRemoveAdmin = async (u) => {
    if (!window.confirm(`Remove admin privileges from "${u.name}"?`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/auth/remove-admin/${u.id}?admin_email=${user.email}`);
      toast.success(`Admin removed from ${u.name}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const handleEditSave = async () => {
    if (!editingUser || !editName.trim()) return;
    try {
      await axios.put(`${BACKEND_URL}/api/auth/users/${editingUser.id}?admin_email=${user.email}`, { name: editName.trim() });
      toast.success('User updated');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const filteredUsers = allUsers.filter(u => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      if (!u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
    }
    if (filterRole === 'admin' && !u.is_admin) return false;
    if (filterRole === 'user' && u.is_admin) return false;
    if (filterStatus !== 'all' && u.status !== filterStatus) return false;
    return true;
  });

  const pendingCount = allUsers.filter(u => u.status === 'pending').length;
  const activeCount = allUsers.filter(u => u.status === 'active').length;
  const inactiveCount = allUsers.filter(u => u.status === 'inactive').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-y-auto text-white">
      {/* Background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-users" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse">
              <path d="M28 0L56 16.5V49.5L28 66L0 49.5V16.5L28 0Z" fill="none" stroke="rgba(139, 92, 246, 0.12)" strokeWidth="1"/>
              <path d="M28 66L56 82.5V115.5L28 132L0 115.5V82.5L28 66Z" fill="none" stroke="rgba(139, 92, 246, 0.12)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-users)" />
        </svg>
      </div>
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(139, 92, 246, 0.06) 0%, transparent 70%)' }} />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-slate-950/90 backdrop-blur-xl border-b border-violet-500/20">
        <div className="h-full max-w-[1920px] mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-400 hover:text-violet-400 hover:bg-violet-500/10" data-testid="back-btn">
              <Home className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-500/10 border border-violet-500/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide">User Management</h1>
                <p className="text-xs text-slate-500">Manage Access, Roles & Security</p>
              </div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-slate-500">Home</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className="text-violet-400 font-medium">Users</span>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 px-4 sm:px-6 pb-8">
        <div className="max-w-[1400px] mx-auto space-y-6">

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Total Users</p>
              <p className="text-2xl font-bold text-white font-mono mt-1">{allUsers.length}</p>
            </div>
            <div className="bg-slate-900/40 border border-green-500/20 rounded-xl p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Active</p>
              <p className="text-2xl font-bold text-green-400 font-mono mt-1">{activeCount}</p>
            </div>
            <div className="bg-slate-900/40 border border-orange-500/20 rounded-xl p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Pending</p>
              <p className="text-2xl font-bold text-orange-400 font-mono mt-1">{pendingCount}</p>
            </div>
            <div className="bg-slate-900/40 border border-red-500/20 rounded-xl p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Inactive</p>
              <p className="text-2xl font-bold text-red-400 font-mono mt-1">{inactiveCount}</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-3">
            <Button
              onClick={() => setActiveTab('users')}
              className={`${activeTab === 'users' ? 'bg-violet-500/20 text-violet-400 border-violet-500/50' : 'bg-slate-900/40 text-slate-400 border-slate-800 hover:border-slate-700'} border font-bold uppercase tracking-wide`}
              data-testid="tab-users"
            >
              <Users className="w-4 h-4 mr-2" />
              Users ({allUsers.length})
            </Button>
            <Button
              onClick={() => { setActiveTab('audit'); fetchAuditTrail(); }}
              className={`${activeTab === 'audit' ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50' : 'bg-slate-900/40 text-slate-400 border-slate-800 hover:border-slate-700'} border font-bold uppercase tracking-wide`}
              data-testid="tab-audit"
            >
              <History className="w-4 h-4 mr-2" />
              Audit Trail
            </Button>
          </div>

          {/* Users Tab */}
          {activeTab === 'users' && (
            <>
              {/* Search & Filters */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or email..."
                    className="w-full pl-10 pr-10 py-2.5 bg-slate-900/60 border border-slate-800 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-violet-500/50 font-mono"
                    data-testid="user-search"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500/50"
                  data-testid="filter-role"
                >
                  <option value="all">All Roles</option>
                  <option value="admin">Admins</option>
                  <option value="user">Users</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500/50"
                  data-testid="filter-status"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* User List */}
              <div className="bg-slate-900/40 backdrop-blur-md border border-violet-500/10 rounded-xl overflow-hidden">
                {/* Table Header */}
                <div className="hidden md:grid grid-cols-12 gap-2 bg-slate-900/80 border-b border-slate-800 px-4 py-3 text-xs text-slate-500 uppercase tracking-wider font-bold">
                  <div className="col-span-3">User</div>
                  <div className="col-span-2">Email</div>
                  <div className="col-span-1">Role</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-1">Last Login</div>
                  <div className="col-span-1">Joined</div>
                  <div className="col-span-3 text-right">Actions</div>
                </div>

                {filteredUsers.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">No users match your filters</div>
                ) : (
                  <div className="divide-y divide-slate-800/50">
                    {filteredUsers.map((u) => (
                      <div key={u.id} className="p-4 hover:bg-violet-500/5 transition-colors" data-testid={`user-row-${u.id}`}>
                        <div className="md:grid md:grid-cols-12 md:gap-2 md:items-center space-y-2 md:space-y-0">
                          {/* Name */}
                          <div className="col-span-3 flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                              u.is_admin ? 'bg-violet-500/20 border border-violet-500/30' : 'bg-slate-800 border border-slate-700'
                            }`}>
                              {u.is_admin ? <Shield className="w-5 h-5 text-violet-400" /> : <Users className="w-5 h-5 text-slate-400" />}
                            </div>
                            <span className="text-white font-bold truncate">{u.name}</span>
                          </div>

                          {/* Email */}
                          <div className="col-span-2 text-slate-400 text-sm font-mono truncate">{u.email}</div>

                          {/* Role */}
                          <div className="col-span-1">
                            {u.is_admin ? (
                              <span className="text-xs px-2 py-0.5 rounded bg-violet-500/20 text-violet-400 border border-violet-500/30 font-bold uppercase">Admin</span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700 font-bold uppercase">User</span>
                            )}
                          </div>

                          {/* Status */}
                          <div className="col-span-1"><StatusBadge status={u.status} /></div>

                          {/* Last Login */}
                          <div className="col-span-1 text-xs text-slate-500">{formatDate(u.last_login)}</div>

                          {/* Joined */}
                          <div className="col-span-1 text-xs text-slate-500">{formatDate(u.created_at)}</div>

                          {/* Actions */}
                          <div className="col-span-3 flex items-center justify-end gap-1 flex-wrap">
                            {u.status === 'pending' && (
                              <>
                                <Button size="sm" onClick={() => handleApprove(u.id)} className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/50 h-8 text-xs" data-testid={`approve-btn-${u.id}`}>
                                  <UserCheck className="w-3.5 h-3.5 mr-1" /> Approve
                                </Button>
                                <Button size="sm" onClick={() => handleDelete(u)} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8 text-xs" data-testid={`reject-btn-${u.id}`}>
                                  <UserX className="w-3.5 h-3.5 mr-1" /> Reject
                                </Button>
                              </>
                            )}

                            {u.status !== 'pending' && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => { setEditingUser(u); setEditName(u.name); }} className="text-cyan-400 hover:bg-cyan-500/10 h-8 w-8 p-0" title="Edit" data-testid={`edit-btn-${u.id}`}>
                                  <Edit className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleResetPassword(u)} className="text-amber-400 hover:bg-amber-500/10 h-8 w-8 p-0" title="Reset Password" data-testid={`reset-btn-${u.id}`}>
                                  <KeyRound className="w-3.5 h-3.5" />
                                </Button>

                                {u.is_admin && u.email !== 'jaco@hotforge.co.za' ? (
                                  <Button size="sm" variant="ghost" onClick={() => handleRemoveAdmin(u)} className="text-violet-400 hover:bg-violet-500/10 h-8 w-8 p-0" title="Remove Admin" data-testid={`remove-admin-btn-${u.id}`}>
                                    <ShieldOff className="w-3.5 h-3.5" />
                                  </Button>
                                ) : !u.is_admin && u.status === 'active' ? (
                                  <Button size="sm" variant="ghost" onClick={() => handleMakeAdmin(u)} className="text-violet-400 hover:bg-violet-500/10 h-8 w-8 p-0" title="Make Admin" data-testid={`make-admin-btn-${u.id}`}>
                                    <Shield className="w-3.5 h-3.5" />
                                  </Button>
                                ) : null}

                                {u.status === 'active' && u.email !== 'jaco@hotforge.co.za' && (
                                  <Button size="sm" variant="ghost" onClick={() => handleDeactivate(u)} className="text-orange-400 hover:bg-orange-500/10 h-8 w-8 p-0" title="Deactivate" data-testid={`deactivate-btn-${u.id}`}>
                                    <UserMinus className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                {u.status === 'inactive' && (
                                  <Button size="sm" variant="ghost" onClick={() => handleActivate(u)} className="text-green-400 hover:bg-green-500/10 h-8 w-8 p-0" title="Reactivate" data-testid={`activate-btn-${u.id}`}>
                                    <UserPlus className="w-3.5 h-3.5" />
                                  </Button>
                                )}

                                {u.email !== 'jaco@hotforge.co.za' && (
                                  <Button size="sm" variant="ghost" onClick={() => handleDelete(u)} className="text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" title="Delete Permanently" data-testid={`delete-btn-${u.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Audit Trail Tab */}
          {activeTab === 'audit' && (
            <div className="bg-slate-900/40 backdrop-blur-md border border-cyan-500/10 rounded-xl overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-2 bg-slate-900/80 border-b border-slate-800 px-4 py-3 text-xs text-slate-500 uppercase tracking-wider font-bold">
                <div className="col-span-2">Date/Time</div>
                <div className="col-span-2">Action</div>
                <div className="col-span-2">Admin</div>
                <div className="col-span-2">Affected User</div>
                <div className="col-span-4">Details</div>
              </div>
              {auditTrail.length === 0 ? (
                <div className="p-12 text-center text-slate-500">No audit trail entries</div>
              ) : (
                <div className="divide-y divide-slate-800/50">
                  {auditTrail.map((entry, idx) => (
                    <div key={idx} className="md:grid md:grid-cols-12 md:gap-2 p-4 hover:bg-cyan-500/5 transition-colors text-sm">
                      <div className="col-span-2 text-slate-400 font-mono text-xs">{formatDateTime(entry.timestamp)}</div>
                      <div className="col-span-2">
                        <span className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${
                          entry.action.includes('Delete') || entry.action.includes('Reject') ? 'bg-red-500/20 text-red-400' :
                          entry.action.includes('Deactivat') ? 'bg-orange-500/20 text-orange-400' :
                          entry.action.includes('Reset') ? 'bg-amber-500/20 text-amber-400' :
                          entry.action.includes('Admin') ? 'bg-violet-500/20 text-violet-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {entry.action}
                        </span>
                      </div>
                      <div className="col-span-2 text-slate-400 truncate">{entry.admin_email}</div>
                      <div className="col-span-2 text-slate-300 font-mono truncate">{entry.target_email}</div>
                      <div className="col-span-4 text-slate-500 text-xs">{entry.details}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Password Reset Result Modal */}
      {resetResult && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-950 border border-amber-500/30 rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl" data-testid="reset-modal">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Password Reset</h3>
                <p className="text-slate-400 text-sm">{resetResult.user_email}</p>
              </div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 mb-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Temporary Password</p>
              <div className="flex items-center justify-between">
                <code className="text-xl font-mono text-amber-400 tracking-wider" data-testid="temp-password">{resetResult.temporary_password}</code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { navigator.clipboard.writeText(resetResult.temporary_password); toast.success('Copied to clipboard'); }}
                  className="text-slate-400 hover:text-white h-8 w-8 p-0"
                  data-testid="copy-password"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-6">Share this password securely with the user. They should change it after logging in.</p>
            <Button onClick={() => setResetResult(null)} className="w-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/50" data-testid="close-reset-modal">
              Done
            </Button>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-950 border border-cyan-500/30 rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl" data-testid="edit-modal">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                <Edit className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Edit User</h3>
                <p className="text-slate-400 text-sm">{editingUser.email}</p>
              </div>
            </div>
            <div className="space-y-4 mb-6">
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">Full Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-900/60 border border-slate-800 rounded-lg text-white focus:outline-none focus:border-cyan-500/50"
                  data-testid="edit-name-input"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setEditingUser(null)} variant="outline" className="flex-1 border-slate-700 text-slate-400">Cancel</Button>
              <Button onClick={handleEditSave} className="flex-1 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/50" data-testid="edit-save-btn">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
